/**
 * Macro #20 — Review Operations API client (admin-only).
 *
 * Discovery + operational review of PUBLIC publications. Post-publication only —
 * nothing here blocks publishing. Reads carry the SIWE session cookie; the Mark
 * Reviewed mutation additionally carries the memory-only session-bound CSRF token
 * (shared with the other admin/owner writes) with a single stale-token retry.
 * Every route independently re-enforces `require_admin` server-side; the client
 * is NEVER the security boundary.
 *
 * The Inspector snapshot is the ONLY admin-authorized moderation bypass; it is
 * reached exclusively through these admin routes and the public surface stays
 * fail-closed.
 */
import { API_BASE } from "./api";
import type { PublicSnapshot } from "./publication-api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export class ReviewApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ReviewApiError";
    this.status = status;
    this.code = code;
  }
}

export type ReviewClassification = "NEW" | "REPUBLISH" | "REVIEWED" | "UNPUBLISHED";
export type ClientStatus = "UNPUBLISHED" | "LIVE_PENDING" | "LIVE_REVIEWED" | "HIDDEN_MODERATION";

export interface ReviewItem {
  identity_id: string;
  owner: string | null;
  current_publication_revision: number;
  last_reviewed_revision: number | null;
  pending: boolean;
  reviewed: boolean;
  classification: ReviewClassification;
  revisions_since_review?: number;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  first_published_at?: string | null;
  current_published_at: string | null;
  updated_at?: string | null;
  version?: number;
  display_name?: string | null;
  moderated?: boolean;
  live?: boolean;
  has_active_publication?: boolean;
  member_parcel_ids?: number[];
  parcel_count?: number;
}

export interface ReviewSummary {
  total: number;
  pending: number;
  new: number;
  republish: number;
  reviewed: number;
}

export interface ReviewClient {
  owner: string;
  parcels: number;
  territories: number;
  published: number;
  pending: number;
  reviewed: number;
  unpublished: number;
  moderated: number;
  latest_publish: string | null;
  has_new: boolean;
  has_republish: boolean;
  status: ClientStatus;
}

export interface ParcelGeom {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ClientDetail {
  owner: string;
  territories: ReviewItem[];
  count: number;
  parcels: number;
  parcels_geom?: ParcelGeom[];
}

export interface ReviewModerationInfo {
  moderated: boolean;
  case_id?: string;
  status?: string;
  reason_category?: string | null;
  emergency?: boolean;
}

export interface Footprint {
  parcel_count: number;
  area: number;
  bbox: { x: number; y: number; w: number; h: number };
}

export interface TerritoryInfo {
  identity_id: string;
  owner: string | null;
  display_name: string | null;
  exists: boolean;
  member_parcel_ids: number[];
  parcel_count: number;
  footprint: Footprint | null;
  review: ReviewItem | null;
  has_active_publication: boolean;
  moderation: ReviewModerationInfo;
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    throw new ReviewApiError(
      resp.status,
      env.code ?? env.detail?.code ?? "REVIEW_ERROR",
      env.message ?? env.detail?.message ?? resp.statusText,
    );
  }
  return body as T;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  return parse<T>(resp);
}

async function csrfPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const payload = JSON.stringify(body);
  const doPost = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body: payload,
    });
  let resp = await doPost(await getCsrfToken());
  if (resp.status === 403) {
    const peek = (await resp.clone().json().catch(() => null)) as ErrorEnvelope | null;
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPost(await getCsrfToken(true));
    }
  }
  return parse<T>(resp);
}

export const reviewApi = {
  queue: (
    opts: { filter?: string; sort?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (opts.filter) q.set("filter", opts.filter);
    if (opts.sort) q.set("sort", opts.sort);
    if (opts.limit) q.set("limit", String(opts.limit));
    const qs = q.toString();
    return get<{ items: ReviewItem[]; count: number }>(
      `/api/v1/admin/review/queue${qs ? `?${qs}` : ""}`,
      signal,
    );
  },
  summary: (signal?: AbortSignal) =>
    get<ReviewSummary>("/api/v1/admin/review/summary", signal),
  clients: (limit = 100, signal?: AbortSignal) =>
    get<{ clients: ReviewClient[]; count: number }>(
      `/api/v1/admin/review/clients?limit=${limit}`,
      signal,
    ),
  clientDetail: (owner: string, signal?: AbortSignal) =>
    get<ClientDetail>(`/api/v1/admin/review/clients/${encodeURIComponent(owner)}`, signal),
  territory: (identityId: string, signal?: AbortSignal) =>
    get<TerritoryInfo>(`/api/v1/admin/review/territory/${encodeURIComponent(identityId)}`, signal),
  snapshot: async (identityId: string, signal?: AbortSignal): Promise<PublicSnapshot | null> => {
    const resp = await fetch(
      `${API_BASE}/api/v1/admin/review/territory/${encodeURIComponent(identityId)}/snapshot`,
      { credentials: "include", cache: "no-store", headers: { Accept: "application/json" }, signal },
    );
    if (resp.status === 404) return null;
    return ((await parse<{ publication: PublicSnapshot }>(resp)).publication) ?? null;
  },
  markReviewed: (identityId: string, expectedPublicationRevision: number) =>
    csrfPost<{ review: ReviewItem }>("/api/v1/admin/review/mark-reviewed", {
      identity_id: identityId,
      expected_publication_revision: expectedPublicationRevision,
    }),
};
